import { useGet } from '@foodics/ui-common';
import { ref, computed, watch, toValue } from 'vue';
import type { ApiRequest } from '@/types/api';
import type { PaginationLinks, PaginationMeta } from '@/types/api/';

type FetchOptions = Parameters<typeof useGet>[1];

type PaginationOptions = {
    fetchOptions: FetchOptions;
    // more options to add later on
};

export function usePagination<
    RequestType extends ApiRequest<
        'GET',
        any,
        { data: any[]; links: PaginationLinks; meta: PaginationMeta }
    >,
>(endPoint: RequestType['url'], options?: PaginationOptions) {
    const currentPage = ref(1);
    const hasFetchedInitialPage = ref(false);

    const fetchOptions = computed(() => {
        const optionsValue = toValue(options?.fetchOptions);

        return {
            ...optionsValue,
            params: {
                ...optionsValue?.params,
                page: currentPage.value,
            },
            disableAutoExecute: true,
        };
    });

    const { data, isLoading, execute, abort } = useGet<RequestType['response']>(
        endPoint,
        fetchOptions,
    );

    watch(
        data,
        (newValue) => {
            if (newValue) {
                hasFetchedInitialPage.value = true;
            }
        },
        { immediate: true },
    );

    const hasNextPage = computed(() => {
        return Boolean(data.value?.links.next);
    });

    const items = computed(() => {
        return (data.value?.data || []) as RequestType['response']['data'];
    });

    function fetchNextPage() {
        if (hasNextPage.value && !isLoading.value) {
            currentPage.value++;

            safeExecute();
        }
    }

    function resetPagination() {
        currentPage.value = 1;
        hasFetchedInitialPage.value = false;

        safeExecute();
    }

    function safeExecute() {
        abort?.();
        execute();
    }

    safeExecute();

    return {
        items,
        isLoading,
        hasNextPage,
        fetchNextPage,
        resetPagination,
        hasFetchedInitialPage,
    };
}
